import React from 'react';
import styles from './GraphTooltip.module.scss';

interface TooltipItem {
    label: string;
    value: any;
    color?: string;
}

interface GraphTooltipProps {
    // Props from Recharts Tooltip content
    active?: boolean;
    payload?: any[];

    // Configuration props
    titleExtractor?: (payload: any[]) => string | undefined | null;
    itemsExtractor?: (payload: any[]) => TooltipItem[];
    titleFormatter?: (title: string) => string;
    valueFormatter?: (value: any, label?: string, color?: string) => string; // Allow context
    className?: string;
}

const defaultTitleExtractor = (payload: any[]): string | undefined => {
    // Common patterns for title/x-axis value
    if (!payload || payload.length === 0) return undefined;
    const firstPayload = payload[0].payload;
    return firstPayload?.x?.toString() || firstPayload?.name;
};

const defaultItemsExtractor = (payload: any[]): TooltipItem[] => {
    if (!payload) return [];
    // Common pattern for line/bar graphs
    return payload.map((item, index) => ({
        label: item.payload?.[`label${index}`] || item.name || `Series ${index + 1}`,
        value: item.value,
        color: item.color,
    }));
};

const defaultValueFormatter = (value: any): string => {
    if (typeof value === 'number') {
        return Math.round(value).toLocaleString();
    }
    return String(value);
};


export const GraphTooltip: React.FC<GraphTooltipProps> = ({
    active,
    payload,
    titleExtractor = defaultTitleExtractor,
    itemsExtractor = defaultItemsExtractor,
    titleFormatter,
    valueFormatter = defaultValueFormatter,
    className = '',
}) => {
    if (!active || !payload || payload.length === 0) {
        return null;
    }

    // Ensure payload exists before passing to extractors
    const title = titleExtractor(payload);
    const items = itemsExtractor(payload);

    const formattedTitle = title && titleFormatter ? titleFormatter(title) : title;

    return (
        <div className={`${styles['tooltip-container']} ${className}`}>
            {formattedTitle && (
                <div className={styles['tooltip-header']}>
                    <div className={styles['tooltip-title']}>{formattedTitle}</div>
                    {/* Add subtitle logic if needed later */}
                </div>
            )}
            <div className={styles['tooltip-content']}>
                {items
                .reverse()
                .map((item, index) => (
                    <div key={index} className={styles['tooltip-item']}>
                        <div className={styles['tooltip-item-header']}>
                            {item.color && (
                                <div
                                    className={styles['tooltip-item-color']}
                                    style={{ backgroundColor: item.color }}
                                />
                            )}
                            <span className={styles['tooltip-item-label']}>
                                {item.label}: {/* Add colon for clarity */}
                            </span>
                        </div>
                        <div className={styles['tooltip-item-value']}>
                            {valueFormatter(item.value, item.label, item.color)}
                        </div>
                        {/* Remove divider for now to match BarGraph/ScatterGraph style? Let's keep it for consistency with LineGraph. */}
                        {/* {index < items.length - 1 && (
                            <div className={styles['tooltip-item-divider']} />
                        )} */}
                    </div>
                ))}
            </div>
        </div>
    );
};


